import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, LogOut } from "lucide-react";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import { api } from "@/api";

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
const fmtDateTime = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export default function Dashboard({ onLogout }) {
  const [stats, setStats] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [launchDate, setLaunchDate] = useState("");
  const [savingDate, setSavingDate] = useState(false);

  const handle401 = useCallback((err) => {
    if (err.response?.status === 401) {
      toast.error("Session expired. Sign in again.");
      onLogout();
      return true;
    }
    return false;
  }, [onLogout]);

  const load = useCallback(async () => {
    try {
      const [s, d, w] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/dealer-inquiries"),
        api.get("/admin/waitlist"),
      ]);
      setStats(s.data);
      setDealers(d.data);
      setWaitlist(w.data);
      if (s.data.launch_date) {
        setLaunchDate(new Date(s.data.launch_date).toISOString().slice(0, 16));
      }
    } catch (err) {
      if (!handle401(err)) toast.error("Failed to load dashboard");
    }
  }, [handle401]);

  useEffect(() => { load(); }, [load]);

  const saveLaunchDate = async () => {
    if (!launchDate || savingDate) return;
    setSavingDate(true);
    try {
      const iso = new Date(launchDate).toISOString();
      await api.put("/admin/settings", { launch_date: iso });
      toast.success("Launch date updated — live on the site now.");
      load();
    } catch (err) {
      if (!handle401(err)) toast.error("Couldn't update launch date");
    } finally {
      setSavingDate(false);
    }
  };

  const download = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      if (!handle401(err)) toast.error("Export failed");
    }
  };

  const statCards = stats ? [
    { id: "dealers", label: "Dealer Applications", value: stats.dealers_total },
    { id: "waitlist", label: "Waitlist Signups", value: stats.waitlist_total },
    { id: "today", label: "Signups Today", value: stats.waitlist_today + stats.dealers_today },
    { id: "launch", label: "Launch Date", value: stats.launch_date ? fmtDate(stats.launch_date) : "—" },
  ] : [];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen px-6 py-10 md:px-12">
      <header className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-4 border-b border-zinc-800 pb-8">
        <div>
          <p className="font-display text-2xl font-bold tracking-[0.2em] text-zinc-50">CHWA</p>
          <p className="mt-1 text-[10px] uppercase tracking-[0.35em] text-zinc-500">Command Center</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/" data-testid="admin-view-site" className="border border-zinc-700 px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-zinc-300 transition-colors duration-300 hover:border-zinc-400">
            View Site
          </Link>
          <button data-testid="admin-logout" onClick={onLogout} className="flex items-center gap-2 border border-zinc-700 px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-zinc-300 transition-colors duration-300 hover:border-red-400 hover:text-red-300">
            <LogOut size={12} /> Logout
          </button>
        </div>
      </header>

      <div className="mx-auto mt-10 max-w-[1400px]">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {statCards.map((s) => (
            <div key={s.id} data-testid={`stat-${s.id}`} className="border border-zinc-800 bg-[#0D0D0D] px-6 py-8">
              <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{s.label}</p>
              <p className="mt-3 font-display text-4xl font-bold text-zinc-50 md:text-5xl">{s.value}</p>
            </div>
          ))}
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          <div data-testid="admin-chart" className="border border-zinc-800 bg-[#0D0D0D] p-6 lg:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Signups — Last 14 Days</p>
            <div className="mt-6 h-64">
              {stats && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.daily.map((d) => ({ ...d, date: fmtDate(d.date) }))} barGap={2}>
                    <XAxis dataKey="date" tick={{ fill: "#52525b", fontSize: 10 }} axisLine={{ stroke: "#27272a" }} tickLine={false} />
                    <Tooltip cursor={{ fill: "#18181b" }} contentStyle={{ background: "#111111", border: "1px solid #27272a", fontSize: 12 }} labelStyle={{ color: "#a1a1aa" }} />
                    <Bar dataKey="waitlist" name="Waitlist" fill="#f4f4f5" />
                    <Bar dataKey="dealers" name="Dealers" fill="#52525b" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div data-testid="launch-date-editor" className="flex flex-col border border-zinc-800 bg-[#0D0D0D] p-6">
            <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Launch Date</p>
            <p className="mt-2 text-xs leading-relaxed text-zinc-600">Updates the live countdown on the homepage and CHWA-01 page.</p>
            <input
              data-testid="launch-date-input"
              type="datetime-local"
              value={launchDate}
              onChange={(e) => setLaunchDate(e.target.value)}
              className="mt-6 w-full border border-zinc-800 bg-[#111111] px-4 py-3 text-sm text-zinc-100 outline-none transition-colors duration-300 focus:border-zinc-400 [color-scheme:dark]"
            />
            <button
              data-testid="launch-date-save"
              onClick={saveLaunchDate}
              disabled={savingDate || !launchDate}
              className="mt-4 bg-zinc-100 px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-zinc-950 transition-colors duration-300 hover:bg-white disabled:opacity-50"
            >
              {savingDate ? "Saving..." : "Update Launch Date"}
            </button>
            <div className="mt-auto pt-8">
              <p className="mb-3 text-[10px] uppercase tracking-[0.3em] text-zinc-500">Export Submissions</p>
              <div className="flex flex-col gap-2">
                <button data-testid="export-dealers-btn" onClick={() => download("/admin/export/dealers.csv", "chwa-dealer-inquiries.csv")} className="flex items-center justify-center gap-2 border border-zinc-700 px-4 py-3 text-[10px] uppercase tracking-[0.25em] text-zinc-300 transition-colors duration-300 hover:border-zinc-300">
                  <Download size={12} /> Dealer Applications (CSV)
                </button>
                <button data-testid="export-waitlist-btn" onClick={() => download("/admin/export/waitlist.csv", "chwa-waitlist.csv")} className="flex items-center justify-center gap-2 border border-zinc-700 px-4 py-3 text-[10px] uppercase tracking-[0.25em] text-zinc-300 transition-colors duration-300 hover:border-zinc-300">
                  <Download size={12} /> Waitlist (CSV)
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div data-testid="recent-dealers-table" className="border border-zinc-800 bg-[#0D0D0D] p-6">
            <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Recent Dealer Applications</p>
            <div className="mt-4 divide-y divide-zinc-800/70">
              {dealers.length === 0 && <p className="py-6 text-sm text-zinc-600">No applications yet.</p>}
              {dealers.slice(0, 10).map((d) => (
                <div key={d.id} className="flex items-baseline justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-zinc-100">{d.company}</p>
                    <p className="truncate text-xs text-zinc-500">{d.name} — {d.email}{d.business_type ? ` — ${d.business_type}` : ""}</p>
                  </div>
                  <span className="shrink-0 text-[10px] uppercase tracking-[0.2em] text-zinc-600">{fmtDateTime(d.created_at)}</span>
                </div>
              ))}
            </div>
          </div>

          <div data-testid="recent-waitlist-table" className="border border-zinc-800 bg-[#0D0D0D] p-6">
            <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Recent Waitlist Signups</p>
            <div className="mt-4 divide-y divide-zinc-800/70">
              {waitlist.length === 0 && <p className="py-6 text-sm text-zinc-600">No signups yet.</p>}
              {waitlist.slice(0, 10).map((w) => (
                <div key={w.id} className="flex items-baseline justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-zinc-100">{w.email}</p>
                    <p className="text-xs text-zinc-500">Source: {w.source || "site"}</p>
                  </div>
                  <span className="shrink-0 text-[10px] uppercase tracking-[0.2em] text-zinc-600">{fmtDateTime(w.created_at)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
